import { atom } from "jotai";

import { BookingWizard } from "../../../types";
import {
  BookingWizardDataAtom,
  CurrentRequestGroupIndexAtom,
} from "../../stores";

export function createGolfRound(
  player: BookingWizard.RoundPlayer,
  index: number,
  bookingWizardData: BookingWizard.GolfBookingWizardData
): BookingWizard.GolfRound {
  return {
    id: null,
    index,
    player,
    type: "Golf",
    courseLayout: bookingWizardData.courseLayout ?? null,
    transportationType: bookingWizardData.transportationTypeId
      ? {
          id: bookingWizardData.transportationTypeId,
        }
      : null,
    rate: bookingWizardData.rateId
      ? {
          id: bookingWizardData.rateId,
        }
      : null,
  };
}

export function createRequestRound(
  player: BookingWizard.RoundPlayer,
  index: number,
  bookingWizardData: BookingWizard.RequestBookingWizardData,
  bookingIndex = 0
): BookingWizard.RequestRound {
  return {
    id: null,
    index,
    bookingIndex,
    player,
    type: "Request",
    rate: bookingWizardData.rateId
      ? {
          id: bookingWizardData.rateId,
        }
      : null,
  };
}

export function createDurationRound(
  player: BookingWizard.RoundPlayer,
  index: number,
  bookingWizardData: BookingWizard.DurationBookingWizardData
): BookingWizard.DurationRound {
  return {
    id: null,
    index,
    player,
    type: "Duration",
    rate: bookingWizardData.playerRateId
      ? {
          id: bookingWizardData.playerRateId,
        }
      : null,
  };
}

export function createLessonRound(
  player: BookingWizard.RoundPlayer,
  index: number,
  bookingWizardData: BookingWizard.LessonBookingWizardData
): BookingWizard.LessonRound {
  return {
    id: null,
    index,
    player,
    type: "Lesson",
    lessonTypeId: bookingWizardData.lessonTypeId,
    instructorId: bookingWizardData.instructorId,
    rate: bookingWizardData.playerRateId
      ? {
          id: bookingWizardData.playerRateId,
        }
      : null,
  };
}

export const addPlayerAtom = atom(
  null,
  (get, set, player: BookingWizard.RoundPlayer) => {
    const bookingWizardData = get(BookingWizardDataAtom);
    if (!bookingWizardData) {
      //@TODO: this is an error
      return;
    }

    if (bookingWizardData.type === "GolfBooking") {
      const existingPlayer = bookingWizardData.rounds.find(
        (round) => round.player.id === player.id
      );
      const isDuplicateAllowed =
        player.clientType === "unknownGuest" ||
        player.clientType === "memberHold";
      if (existingPlayer && !isDuplicateAllowed) {
        return;
      }
      const golfRounds = bookingWizardData.rounds;
      const newRound = createGolfRound(
        player,
        golfRounds.length,
        bookingWizardData
      );
      const newRounds = [...golfRounds, newRound];
      set(BookingWizardDataAtom, {
        ...bookingWizardData,
        rounds: newRounds,
      });
    } else if (bookingWizardData.type === "RequestBooking") {
      if (bookingWizardData.rounds.find((r) => r.player.id === player.id)) {
        return;
      }
      const requestRounds = bookingWizardData.rounds;
      const bookingIndex = get(CurrentRequestGroupIndexAtom);
      const currentGroupRounds = requestRounds.filter(
        (round) => (round.bookingIndex ?? 0) === bookingIndex
      );
      const newRound = createRequestRound(
        player,
        currentGroupRounds.length,
        bookingWizardData,
        bookingIndex
      );
      const newRounds = [...requestRounds, newRound];
      set(BookingWizardDataAtom, {
        ...bookingWizardData,
        rounds: newRounds,
      });
    } else if (bookingWizardData.type === "DurationBooking") {
      const existingPlayer = bookingWizardData.rounds.find(
        (round) => round.player.id === player.id
      );
      const isDuplicateAllowed =
        player.clientType === "unknownGuest" ||
        player.clientType === "memberHold";
      if (existingPlayer && !isDuplicateAllowed) {
        return;
      }
      const durationRounds = bookingWizardData.rounds;
      const newRound = createDurationRound(
        player,
        durationRounds.length,
        bookingWizardData
      );
      const newRounds = [...durationRounds, newRound];
      set(BookingWizardDataAtom, {
        ...bookingWizardData,
        rounds: newRounds,
      });
    } else if (bookingWizardData.type === "LessonBooking") {
      if (bookingWizardData.rounds.find((r) => r.player.id === player.id)) {
        return;
      }
      const lessonRounds = bookingWizardData.rounds;
      const newRound = createLessonRound(
        player,
        lessonRounds.length,
        bookingWizardData
      );
      const newRounds = [...lessonRounds, newRound];
      set(BookingWizardDataAtom, {
        ...bookingWizardData,
        rounds: newRounds,
      });
    }
  }
);
